import { TAB_TYPE, Tab } from '@repo/design-system/index'
import classNames from 'classnames'
import { memo, useEffect, useState } from 'react'
import { useGTMBasicPageViewEventWithProperties } from '../../helpers/gtmHelper'
import useIsMobile from '../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../modules/hooks/common/useIsWebview'
import { n_lesson_prep, s_materials, s_preparation } from '../../modules/i18n/strings/auto/wording'
import useTranslation from '../../modules/i18n/useTranslation'
import { STUDENT_PAGE_VIEW_ID } from '../../modules/ids/gtm/student'
import { track } from '../../modules/track'
import { LOCATION, LOG_CATEGORY, LOG_TYPE } from '../../modules/vars'
import { TextProvider } from '../../pages/[lang]/portal/materials/detail/[materialId]'
import EmptyBlock from '../common/EmptyBlock'
import ResizableImage from '../common/ResizableImage'
import TextLinkHorizontal from '../common/card/TextLinkHorizontal'
import ExpressionPanel from '../new-portal/materials/ExpressionPanel'
import MaterialInsightBody from '../new-portal/materials/MaterialInsightBody'

export const QuestionPanel = ({ questions, isShort = false }) => {
  return (
    <div className="pb-[120px] sm:px-20 sm:pb-[0px] lg:px-0">
      <div className="">
        {questions?.map((item, i) => {
          if (isShort && i > 2) return //랜딩 컨텐츠 질문 3개만 보여줍니다.
          return (
            <div key={item.id} className="mb-[20px] border-b">
              <div className="mb-[48px] flex flex-row">
                <div className="">
                  <div className="Caption-12Bd rounded-[4px] bg-purple-50 px-[6px] py-[2px] text-purple-500">
                    Q{i + 1}
                  </div>
                </div>

                <div className="text-sBody md:text-16 px-[32px]">
                  {item.text ? item.text : item.content_main}
                  <div className="w-1/4">
                    {item.img_url ? (
                      <div className="mt-[24px]">
                        <ResizableImage url={item.img_url} />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const MediaPanel = ({ media, materialId, className = '' }) => {
  const { t } = useTranslation()

  const handleArticleClick = (item) => {
    track({
      location: LOCATION.COURSE,
      log_type: LOG_TYPE.COURSE.ARTICLE,
      log_category: LOG_CATEGORY.READ,
      course_id: materialId,
      article_id: item.id,
    })
  }
  return (
    <div className={classNames(className, 'w-full px-[20px] pb-[16px] sm:px-[0px]')}>
      {media?.youtubes?.length > 0 && (
        <div className="Title1-20Bd mb-[16px] text-gray-600">{t(n_lesson_prep.complete.related_video)}</div>
      )}
      <div className="mb-[32px] flex flex-col gap-[16px]">
        {media.youtubes?.map((item, i) => (
          <div className="aspect-[16/9]" key={i}>
            <iframe
              width="100%"
              height="100%"
              className="aspect-[16/9]"
              src={`https://www.youtube.com/embed/${item.code}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        ))}
      </div>
      {media.articles?.length > 0 && (
        <div className="Title1-20Bd mb-[16px] text-gray-600">{t(n_lesson_prep.complete.related_news)}</div>
      )}
      {media.articles?.map((item, i) => (
        <TextLinkHorizontal item={item} key={i} onClick={handleArticleClick} />
      ))}
    </div>
  )
}
export const MaterialModalSubTab = ({ activeTabIdx, setActiveTabIdx, className = '' }) => {
  const { t } = useTranslation()

  const SUB_MENU_LIST = [
    {
      label: t(s_preparation.insight),
    },
    {
      label: t(n_lesson_prep.complete.tab_expression),
    },
    {
      label: t(s_materials.key_questions),
    },
    {
      label: t(s_materials.clip_article),
    },
  ]

  return (
    <div className={classNames(className, 'border-b-1 z-[20] ml-[0px] w-screen bg-white sm:w-full')}>
      <div className="mx-auto max-w-[1040px]">
        <Tab
          className="h-[46px] overflow-hidden"
          type={TAB_TYPE.SCROLL}
          borderNone={true}
          activeTabIdx={activeTabIdx}
          setActiveTabIdx={setActiveTabIdx}
        >
          {SUB_MENU_LIST.map((subMenuItem, i) => {
            return <Tab.TabItem name={subMenuItem.label} key={i} />
          })}
        </Tab>
      </div>
    </div>
  )
}

interface IMaterialBodyComponent {
  activeTabIdx: number
  materialData?
  materialId: any
  isInsightButton?: boolean
  isModal?: boolean
}

const NEW_TAB = {
  INSIGHT: 0,
  QUESTIONS: 2,
  MEDIA: 3,
  EXPRESSIONS: 1,
}

export const ModalBodyComponent = ({
  activeTabIdx,
  materialData,
  materialId,
  isModal = false,
}: IMaterialBodyComponent) => {
  const isWebview = useIsWebview()
  const isMobile = useIsMobile()
  const media = {
    youtubes: materialData?.course?.video,
    articles: materialData?.course?.article,
  }

  switch (activeTabIdx) {
    case NEW_TAB.INSIGHT:
      return <MaterialInsightBody materialData={materialData} materialId={materialId} isModal={isModal} />
    case NEW_TAB.EXPRESSIONS:
      return <ExpressionPanel data={materialData} isShort={false} />
    case NEW_TAB.QUESTIONS:
      return (
        <div className={classNames((isWebview || isMobile) && 'px-[16px]')}>
          <QuestionPanel questions={materialData?.keyquestions} isShort={false} />
          <EmptyBlock h={60} />
        </div>
      )
    case NEW_TAB.MEDIA:
      return <MediaPanel media={media} materialId={materialId} />
    default:
      return <MaterialInsightBody materialData={materialData} materialId={materialId} />
  }
}

const MaterialModal = ({ data, isTrial = false, isMobile }: { data: any; isTrial?: boolean; isMobile?: boolean }) => {
  const [activeTabIdx, setActiveTabIdx] = useState<number>(0)

  const sendGTMPVEvent = useGTMBasicPageViewEventWithProperties({
    page_id: STUDENT_PAGE_VIEW_ID.TRIAL_MATERIAL_DETAIL,
    page_title: 'material_detail',
    device_type: 'pc_web',
  })

  useEffect(() => {
    if (isTrial) {
      sendGTMPVEvent()
    }
  }, [isTrial])

  return (
    <>
      {data && (
        <TextProvider>
          <div className="relative mx-auto h-full min-h-[calc(84vh_-_98px)] w-full border-gray-300">
            <div className="flex h-full w-full flex-col rounded-[4px]">
              <MaterialModalSubTab activeTabIdx={activeTabIdx} setActiveTabIdx={setActiveTabIdx} />
              <div className="mt-[40px]">
                <ModalBodyComponent
                  isModal={true}
                  activeTabIdx={activeTabIdx}
                  materialData={data}
                  materialId={data?.course?.id}
                />
              </div>
            </div>
          </div>
        </TextProvider>
      )}
    </>
  )
}

export default memo(MaterialModal)
